import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { RadioComponentSwitchFilled } from '@openx/components-core/lib/RadioComponentSwitchFilled/RadioComponentSwitchFilled.tsx';
import { RHFTextField } from '@openx/components-core/lib/TextField/RHFTextField.tsx';
import { AdFormat } from '@openx/types/gqlTypes.ts';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BannerDimensions } from './components/BannerDimensions.tsx';
import { BannerThumbnail } from './components/BannerThumbnail.tsx';
import { VideoThumbnail } from './components/VideoThumbnail.tsx';
import { getFormatOptions, normalizeFormValuesByFormat } from './utils.tsx';
import type { BannerCreativeFormData } from './validation/bannerCreative.ts';
import type { CreativeFormState } from './validation/validationSchema.ts';

export const CreativeContentSection = () => {
  const { t } = useTranslation();
  const { control, getValues, reset, setValue } = useFormContext<CreativeFormState>();

  const observableAdFormat = useWatch({ control, name: 'ad_format' });

  const formatOptions = useMemo(() => getFormatOptions(t), [t]);

  const isBannerFormat = observableAdFormat === AdFormat.Banner;
  const isVideoFormat = observableAdFormat === AdFormat.Video;

  const handleFormatChange = (newFormat: AdFormat) => {
    const currentValues = getValues();
    const normalizedValues = normalizeFormValuesByFormat(currentValues, newFormat);

    reset(normalizedValues, { keepDefaultValues: true });
  };

  return (
    <FormSection divider title={t('Creative Content')} customGrid>
      <Grid size={5}>
        <Box
          sx={{
            mb: 2,
          }}
        >
          <Typography
            variant="h3"
            sx={{
              mb: 1.5,
            }}
          >
            {t('Format Type')}
          </Typography>

          <Box
            sx={{
              '& .MuiBox-root': {
                display: 'flex',
              },
              '& .MuiFormControlLabel-label > .MuiStack-root': {
                flexDirection: 'row !important',
                alignItems: 'center',
                gap: 1,
              },
            }}
          >
            <RadioComponentSwitchFilled
              value={observableAdFormat}
              radioOptions={formatOptions}
              onSwitchChange={value => handleFormatChange(value as AdFormat)}
            />
          </Box>
        </Box>

        <RHFTextField
          name={'markup_payload' satisfies keyof CreativeFormState}
          label={t('Ad Markup Payload')}
          placeholder={t('Enter Markup Payload (HTML)')}
          fullWidth
          multiline
          rows={4}
          dataTest="markup-payload"
          margin="dense"
          onChange={value => {
            if (isBannerFormat && !value) {
              setValue('width' satisfies keyof BannerCreativeFormData, '0', {
                shouldDirty: true,
                shouldValidate: true,
              });
              setValue('height' satisfies keyof BannerCreativeFormData, '0', {
                shouldDirty: true,
                shouldValidate: true,
              });
            }
          }}
        />

        {isBannerFormat && <BannerDimensions />}
      </Grid>

      <Grid size={1} />

      <Grid size={3}>
        {isBannerFormat && <BannerThumbnail />}
        {isVideoFormat && <VideoThumbnail />}
      </Grid>
    </FormSection>
  );
};
